import { useCallback } from 'react';
import { PulsingBars } from '@core/assets/PulsingBars';
import { useContextTranslation } from '@core/i18n';
import { isStreamingReplay } from '@core/livestream/helpers';
import { StyledContainer, StyledCTAButton, StyledCTAIcon, StyledCTAText, } from '@embed/components/VideoPlayer/Player3/components/overlays/CTA';
import { useActivateOrRedirectCallback } from '@embed/components/VideoPlayer/Player3/hooks/useActivateOrRedirectCallback';
import { useMuting } from '@embed/components/VideoPlayer/Player3/hooks/useMuting';
import { useStore } from '@embed/components/VideoPlayer/Player3/store';
import { useVideoPlayerStore } from '@embed/components/VideoPlayer/store';
import { videoPlayerAvailableWidthSelector, videoPlayerIsMinimizedSelector, } from '@embed/components/VideoPlayer/store/selectors';
import { SpeakerMute40x40 } from '@player/assets/SpeakerMute40x40';
import { PlayerInterruptEvent } from '@player/features/interrupts/types';
import { useInterruptibleCallback } from '@player/features/interrupts/useInterruptibleCallback';
import { LIVESTREAM_ENTER_STREAM_CTA, LIVESTREAMING_ACTIVE_USER_CTA_INACTIVE_USER_MUTED_ICON, } from '@player/testIds';
import { usePlayerTracking } from '@player/tracking/usePlayerTracking';
/**
 * LiveStream CTA to activate user - Inactive user variant
 *
 * Inactive user sees "Tap to watch" where clicking on turns them active
 * or redirects to first party url (if applicable).
 *
 * @returns
 */
export const LiveStreamingActiveUserCTAInactiveUser = () => {
    const isMinimized = useVideoPlayerStore(videoPlayerIsMinimizedSelector);
    const availableWidth = useVideoPlayerStore(videoPlayerAvailableWidthSelector);
    const liveStreamState = useStore((state) => state.liveStreamState);
    const video = useStore((state) => state.video);
    const { t } = useContextTranslation();
    const { trackMinimizedPlayerClick } = usePlayerTracking();
    const onClick = useActivateOrRedirectCallback();
    const onClickWithTracking = useInterruptibleCallback(PlayerInterruptEvent.onLivestreamEnterCallback, useCallback((event) => {
        trackMinimizedPlayerClick({ video });
        onClick(event);
    }, [onClick, trackMinimizedPlayerClick, video]), useCallback((event) => {
        event.stopPropagation();
        event.preventDefault();
    }, []));
    const largeVariant = availableWidth > 250 && !isMinimized;
    const { isVideoMuted } = useMuting();
    return isMinimized ? (<StyledContainer className="cy-enter-live-stream" onClick={onClickWithTracking} aria-label={t('player:Tap to enter livestream')}>
        {isVideoMuted ? (<StyledCTAIcon className="cy-enter-live-stream-min" data-testid={LIVESTREAMING_ACTIVE_USER_CTA_INACTIVE_USER_MUTED_ICON} size={44}>
            <SpeakerMute40x40 label="" aria-hidden/>
          </StyledCTAIcon>) : null}
      </StyledContainer>) : (<StyledContainer className="cy-enter-live-stream" onClick={onClickWithTracking}>
        <StyledCTAButton>
          {largeVariant ? (<>
              <PulsingBars width={14} height={14} label={isStreamingReplay(liveStreamState)
                ? t('player:Tap to watch')
                : t('player:Tap to enter livestream')}/>
              <StyledCTAText data-testid={LIVESTREAM_ENTER_STREAM_CTA}>
                {isStreamingReplay(liveStreamState)
                ? t('player:Tap to watch')
                : t('player:Tap to enter livestream')}
              </StyledCTAText>
            </>) : (<StyledCTAText data-testid={LIVESTREAM_ENTER_STREAM_CTA}>
              {isStreamingReplay(liveStreamState)
                ? t('player:Watch')
                : t('player:Enter')}
            </StyledCTAText>)}
        </StyledCTAButton>
      </StyledContainer>);
};
